import React, { useState, useEffect, useCallback } from "react";
import debounce from "lodash/debounce";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Autocomplete,
    TextField,
    Divider,
} from "@mui/material";
import { RouteSharp } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import AppForm from "components/Shared/AppForm";
import {
    useLazySearchShipmentWithBranchQuery,
    useAssignBulkRoutesMutation,
} from "store/services/shipmentApi";
import { useLazyGetDDLRouteShipmentsQuery } from "store/services/routeShipmentApi";

const BulkRoutesModalForm = ({ open, onClose, accountId, entityId }) => {
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [searchInput, setSearchInput] = useState("");

    // RTK Hooks
    const [triggerSearch, { data: shipments = [], isFetching: loadingShipments }] =
        useLazySearchShipmentWithBranchQuery();

    const [fetchRoutes, { data: routesResponse, isLoading: loadingRoutes }] =
        useLazyGetDDLRouteShipmentsQuery();

    const [assignBulkRoutes, { isLoading: isSubmitting }] =
        useAssignBulkRoutesMutation();

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: { shipments: [], route: null },
    });

    const selectedShipments = watch("shipments");
    const selectedRoutes = watch("routes");

    // Extract routes data based on your API response structure
    const routes = routesResponse?.data || routesResponse || [];

    // FIXED: Proper debounced search with correct dependencies
    const debouncedSearch = useCallback(
        debounce((term) => {
            if (accountId && entityId) {
                triggerSearch({
                    account: accountId,
                    entity: entityId,
                    query: term || ""
                });
            }
        }, 400),
        [accountId, entityId, triggerSearch]
    );

    // FIXED: Improved useEffect for search
    useEffect(() => {
        if (open && accountId && entityId) {
            debouncedSearch(searchInput);
        }

        return () => debouncedSearch.cancel();
    }, [searchInput, debouncedSearch, open, accountId, entityId]);

    // Load routes only on open
    useEffect(() => {
        if (open && accountId && entityId) {
            fetchRoutes({ account: accountId, entity: entityId });
            reset({ shipments: [], route: null });
            setSearchInput("");
            setError("");
            setSuccess("");

            // Trigger initial search with empty query to get some shipments
            triggerSearch({
                account: accountId,
                entity: entityId,
                query: ""
            });
        }
    }, [open, accountId, entityId, fetchRoutes, reset, triggerSearch]);

    const onSubmit = async (data) => {
        if (!data.shipments.length || !data.routes) {
            setError("Please select at least one shipment and a route.");
            return;
        }

        try {
            const payload = {
                shipmentIds: data.shipments.map((s) => s._id),
                routeIds: [data.routes._id],
                account: accountId,
                entity: entityId,
            };
            await assignBulkRoutes(payload).unwrap();
            setSuccess(`Routes assigned to ${data.shipments.length} shipment(s)!`);
            setTimeout(onClose, 1500);
        } catch (err) {
            setError(err?.data?.message || "Failed to assign routes.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    background: (theme) =>
                        theme.palette.mode === "dark"
                            ? "linear-gradient(90deg, rgb(167, 212, 248), rgb(146, 195, 241))"
                            : "linear-gradient(90deg, #1976d2, #42a5f5)",
                    color: "white",
                    fontWeight: 600,
                    py: 2,
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <RouteSharp />
                    <span>Bulk Assign Routes</span>
                </Box>
            </DialogTitle>
            <Divider />

            <AppForm loading={isSubmitting}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent sx={{ p: 3 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                                {error}
                            </Alert>
                        )}
                        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {/* Shipment Search */}
                            <Controller
                                name="shipments"
                                control={control}
                                rules={{ required: "Select at least one shipment" }}
                                render={({ field }) => (
                                    <Autocomplete
                                        multiple
                                        options={shipments?.data || []}
                                        loading={loadingShipments}
                                        value={field.value ?? []}
                                        onChange={(_, value) => field.onChange(value)}
                                        onInputChange={(_, newInput) => {
                                            setSearchInput(newInput);
                                        }}
                                        getOptionLabel={(opt) =>
                                            `${opt.shipmentId} (${opt.sourceBranch?.name || "N/A"} to ${opt.destinationBranch?.name || "N/A"
                                            })`
                                        }
                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Search Shipments"
                                                placeholder="Type shipment ID to search..."
                                                variant="outlined"
                                                error={!!errors.shipments}
                                                helperText={
                                                    errors.shipments?.message ||
                                                    `${field.value?.length ?? 0} selected`
                                                }
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                            />
                                        )}
                                    />
                                )}
                            />

                            <Controller
                                name="routes"
                                control={control}
                                rules={{ required: "Select a route" }}
                                render={({ field }) => (
                                    <Autocomplete
                                        {...field}
                                        options={routes}
                                        loading={loadingRoutes}
                                        getOptionLabel={(opt) => opt?.routeName || ""}
                                        isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                        onChange={(_, value) => field.onChange(value)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Route"
                                                placeholder="Select a route..."
                                                variant="outlined"
                                                error={!!errors.routes}
                                                helperText={errors.routes?.message || (field.value ? "1 selected" : "0 selected")}
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={onClose} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<RouteSharp />}
                            disabled={isSubmitting || !selectedShipments?.length || !selectedRoutes}
                        >
                            {isSubmitting ? "Assigning..." : "Assign Routes"}
                        </Button>
                    </DialogActions>
                </form>
            </AppForm>
        </Dialog >
    );
};

export default BulkRoutesModalForm;